"use client"

import type { FunctionComponent, PropsWithChildren } from "react"

import classNames from "classnames"
import { useEffect, useRef, useState } from "react"

import { DOM_REF_IDS } from "@/modules/jobAdDetail/constants"
import { LayoutChild } from "@/modules/layouts/components/Layout/LayoutChild"

import styles from "./SlideBarCandidateActions.module.css"

const SlideBarCandidateActions: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const callbackCalledTimesRef = useRef(0)
  const slidebarRef = useRef<HTMLDivElement>(null)
  const [isTopAnchorVisible, setIsTopAnchorVisible] = useState(true)
  const [isBottomAnchorVisible, setIsBottomAnchorVisible] = useState(true)
  const [isSlidebarDisappearing, setIsSlidebarDisappearing] = useState(false)

  const isSlidebarVisible = isSlidebarDisappearing || (!isTopAnchorVisible && !isBottomAnchorVisible)

  useEffect(() => {
    const aside = slidebarRef.current && slidebarRef.current.parentNode

    const handleDisappearanceAnimationEnd = (): void => {
      setIsSlidebarDisappearing(false)
    }

    if (aside && isSlidebarDisappearing) aside.addEventListener("animationend", handleDisappearanceAnimationEnd)

    return () => {
      if (aside && isSlidebarDisappearing) aside.removeEventListener("animationend", handleDisappearanceAnimationEnd)
    }
  }, [isSlidebarDisappearing])

  useEffect(() => {
    const topTarget = document.getElementById(DOM_REF_IDS.ACTIONS_TOP)
    const bottomTarget = document.getElementById(DOM_REF_IDS.ACTIONS_BOTTOM)

    const callback: IntersectionObserverCallback = entries => {
      callbackCalledTimesRef.current = callbackCalledTimesRef.current + 1

      entries.forEach(entry => {
        if (entry.target === topTarget) {
          if (entry.isIntersecting === true && isSlidebarVisible) {
            setIsSlidebarDisappearing(true)
          }
          setIsTopAnchorVisible(!!entry.isIntersecting)
        }
        if (entry.target === bottomTarget) {
          setIsBottomAnchorVisible(!!entry.isIntersecting)
        }
      })
    }

    const observer = new IntersectionObserver(callback)

    if (topTarget) {
      observer.observe(topTarget)
    }

    if (bottomTarget) {
      observer.observe(bottomTarget)
    }

    return () => {
      if (topTarget) {
        observer.unobserve(topTarget)
      }
      if (bottomTarget) {
        observer.unobserve(bottomTarget)
      }
    }
  }, [isSlidebarVisible])

  if (!isSlidebarVisible) return null

  return (
    <LayoutChild
      className={classNames(styles.main, {
        [styles.main___appearing]: isSlidebarVisible,
        [styles.main___disappearing]: isSlidebarDisappearing,
      })}
      data-testid="jobad-DetailView__SlideBarCandidateActions"
      variant="large"
    >
      <div className={styles.contentBox} ref={slidebarRef}>
        {children}
      </div>
    </LayoutChild>
  )
}

// We need a default export for next/dynamic inside App Router
// eslint-disable-next-line import/no-default-export
export default SlideBarCandidateActions
